import React, { useId } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import OverflowText from './OverflowText';
import { OVER_BOARD_Z, raiseAboveBoard } from './overBoardTheme';

const WIDTHS = { sm: 480, md: 720, lg: 960, xl: 1200 };

/**
 * FieldtimeDialog — standard dialog shell (UI Design Standards §4.4):
 * header (title, subtitle, meta, status, close X), scrolling body, and a
 * footer that appears only when `actions` is passed.
 *
 * Props:
 *   open, onClose    as MUI Dialog; the close X calls onClose(event, 'closeButton')
 *   title            {node}   required
 *   subtitle         {node}   optional, usually "ID — Name"
 *   meta             {node}   optional caption line
 *   status           {node}   optional, e.g. a StatusPill right of the title
 *   size             'sm' 480 | 'md' 720 (default) | 'lg' 960 | 'xl' 1200
 *   overBoard        {bool}   render above Bryntum popups, including anything
 *                             that pops up inside the dialog (menus, tooltips)
 *   footerCaption    {node}   optional footer text on the left
 *   actions          {node}   footer buttons: Cancel (text), then one contained primary
 *   PaperProps, ...  passed through to Dialog
 */
const FieldtimeDialog = ({
  open,
  onClose,
  title,
  subtitle,
  meta,
  status,
  size = 'md',
  overBoard = false,
  footerCaption,
  actions,
  children,
  PaperProps,
  sx,
  ...dialogProps
}) => {
  const { ft } = useTheme().palette;
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const subtitleId = `${baseId}-subtitle`;

  const dialog = (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby={titleId}
      aria-describedby={subtitle ? subtitleId : undefined}
      sx={overBoard ? { zIndex: OVER_BOARD_Z.modal, ...sx } : sx}
      {...dialogProps}
      PaperProps={{
        ...PaperProps,
        sx: {
          width: WIDTHS[size] ?? WIDTHS.md,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: '85vh',
          m: 2,
          borderRadius: `${ft.radius.card}px`,
          display: 'flex',
          flexDirection: 'column',
          // Body and footer buttons; the dialog portals outside page-level styles.
          '& .MuiButton-root': { textTransform: 'none', fontWeight: 500 },
          ...PaperProps?.sx,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 3,
          pt: 2,
          pb: 1.5,
          borderBottom: `1px solid ${ft.neutrals.divider}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <OverflowText
              variant="dialogTitle"
              component="h2"
              id={titleId}
              sx={{ m: 0, color: ft.neutrals.textPrimary }}
            >
              {title}
            </OverflowText>
            {status && <Box sx={{ flexShrink: 0, display: 'flex' }}>{status}</Box>}
          </Box>
          {subtitle && (
            <OverflowText
              variant="subtitle"
              component="p"
              id={subtitleId}
              sx={{ mt: 0.25, color: ft.neutrals.textSecondary }}
            >
              {subtitle}
            </OverflowText>
          )}
          {meta && (
            <Typography
              variant="caption"
              component="p"
              sx={{ mt: 0.25, color: ft.neutrals.textSecondary }}
            >
              {meta}
            </Typography>
          )}
        </Box>
        <Tooltip title="Close">
          <IconButton
            aria-label="Close"
            size="small"
            onClick={(event) => onClose?.(event, 'closeButton')}
            sx={{ mt: -0.25, mr: -1, color: ft.neutrals.textSecondary }}
          >
            <CloseOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogContent sx={{ px: 3, py: 2, color: ft.neutrals.textBody }}>
        {children}
      </DialogContent>

      {actions && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 3,
            py: 1.5,
            borderTop: `1px solid ${ft.neutrals.divider}`,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {footerCaption && (
              <OverflowText
                variant="caption"
                component="p"
                sx={{ color: ft.neutrals.textSecondary }}
              >
                {footerCaption}
              </OverflowText>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {actions}
          </Box>
        </Box>
      )}
    </Dialog>
  );

  return overBoard ? (
    <ThemeProvider theme={raiseAboveBoard}>{dialog}</ThemeProvider>
  ) : (
    dialog
  );
};

export default FieldtimeDialog;
